import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';

// Utils
import formatHolidaysData from 'utils/format-holidays-data';
import formatScheduleData from 'utils/format-schedule-data';
import { supabase } from 'utils/supabase-client';

import { clearUserData, setUserData } from './userSlice';


const createRestaurant = createAsyncThunk(
  'products/createRestaurant',
  async ({ restaurantData }, { dispatch, rejectWithValue }) => {
    /* eslint-disable camelcase */
    const formattedRestaurantData = {
      address: restaurantData.address || null,
      category: restaurantData.category || null,
      color: restaurantData.color || null,
      country: restaurantData.country || null,
      currency: restaurantData.currency || null,
      description: restaurantData.description || null,
      domain_config: restaurantData.domainConfig || null,
      email: restaurantData.email || null,
      facebook_handle: restaurantData.facebookHandle || null,
      footnote: restaurantData.footnote || null,
      instagram_handle: restaurantData.instagramHandle || null,
      locale: restaurantData.locale || null,
      name: restaurantData.name || null,
      phone_number: restaurantData.phoneNumber || null,
      slug: restaurantData.slug || null,
      twitter_handle: restaurantData.twitterHandle || null,
      user_id: restaurantData.userId || null
    };
    /* eslint-enable camelcase */

    try {
      const response = await supabase
        .from('restaurants')
        .insert(formattedRestaurantData)
        .select(`
          address,
          category,
          color,
          country,
          createdAt:created_at,
          currency,
          description,
          footnote,
          email,
          facebookHandle:facebook_handle,
          id,
          instagramHandle:instagram_handle,
          locale,
          name,
          phoneNumber:phone_number,
          slug,
          twitterHandle:twitter_handle,
          updatedAt:updated_at,
          coverPhoto:cover_photo,
          logo,
          userId:user_id,
          domainConfig:domain_config
        `)
        .single();

      const {
        error
      } = response;

      if (error) {
        throw error;
      }

      const createdRestaurant = response.data;

      // Register subdomain on Vercel
      // This happens asynchronously in the background via Edge Function
      if (createdRestaurant?.id) {
        supabase.functions
          .invoke('register-restaurant-subdomain', {
            body: { restaurantId: createdRestaurant.id }
          })
          .then(({ data, error: subdomainError }) => {
            if (subdomainError) {
              // eslint-disable-next-line no-console
              console.warn('Subdomain registration failed:', subdomainError);
            } else if (data?.success) {
              // eslint-disable-next-line no-console
              console.log('Subdomain registered:', data.domain);
            }
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('Subdomain registration error:', err);
          });
      }

      dispatch(setUserData({
        hasRestaurant: true
      }));

      return createdRestaurant;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const fetchRestaurants = createAsyncThunk(
  'restaurants/fetchRestaurants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await supabase.rpc('get_restaurants');

      const { error, data } = response;

      if (error) {
        throw error;
      }

      if (!Array.isArray(data)) {
        // eslint-disable-next-line no-console
        console.error('Unexpected response format: expected array, got', typeof data);

        return [];
      }

      const formattedRestaurants = data.map((restaurant) => {
        if (!restaurant || typeof restaurant !== 'object') {
          // eslint-disable-next-line no-console
          console.warn('Invalid restaurant data:', restaurant);

          return null;
        }

        return {
          ...restaurant,
          addresses: Array.isArray(restaurant.addresses) ? restaurant.addresses : [],
          holidays: formatHolidaysData(restaurant.scheduleClosedEvents || []),
          schedule: formatScheduleData(restaurant.scheduleDayBlocks || [])
        };
      }).filter(Boolean);

      return formattedRestaurants;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const updateRestaurant = createAsyncThunk(
  'products/updateRestaurant',
  async (data, { rejectWithValue }) => {
    const {
      restaurantId,
      updatedRestaurantData
    } = data;

    if (!restaurantId) {
      return rejectWithValue({ error: 'Restaurant ID is required for update operation' });
    }

    updatedRestaurantData.append('restaurant_id', restaurantId);

    try {
      const response = await supabase.functions.invoke('update-restaurant', {
        body: updatedRestaurantData
      });

      const { data: responseData } = response;

      const {
        data: restaurant,
        error,
        imageUploadErrorsByField
      } = responseData;

      if (error) {
        throw error;
      }

      if (!restaurant) {
        throw new Error('No restaurant data returned from update operation');
      }

      const formattedRestaurant = {
        ...restaurant,
        addresses: Array.isArray(restaurant?.addresses) ? restaurant.addresses : [],
        holidays: formatHolidaysData(restaurant?.scheduleClosedEvents),
        schedule: formatScheduleData(restaurant?.scheduleDayBlocks)
      };

      return {
        imageUploadErrorsByField,
        restaurant: formattedRestaurant
      };
    } catch (error) {
      return rejectWithValue({ error: error?.message });
    }
  }
);

// Immediate, owner-scoped save for the sidebar ordering quick controls (on/off toggle
// and "pause for today"). Calls the lightweight set_restaurant_orders_state RPC directly
// rather than the heavier update-restaurant edge function.
const setRestaurantOrdersState = createAsyncThunk(
  'restaurants/setRestaurantOrdersState',
  async ({ restaurantId, ordersEnabled = null, pause = null, pauseUntil = null }, { rejectWithValue }) => {
    if (!restaurantId) {
      return rejectWithValue({ error: 'Restaurant ID is required' });
    }

    try {
      const { data, error } = await supabase.rpc('set_restaurant_orders_state', {
        /* eslint-disable camelcase */
        p_orders_enabled: ordersEnabled,
        p_pause: pause,
        p_pause_until: pauseUntil,
        p_restaurant_id: restaurantId
        /* eslint-enable camelcase */
      });

      if (error) throw error;

      return data;
    } catch (error) {
      return rejectWithValue({ error: error?.message });
    }
  }
);

const validateRestaurantSlug = createAsyncThunk(
  'restaurants/validateRestaurantSlug',
  async (slug, { rejectWithValue }) => {
    const response = await supabase.rpc('validate_restaurant_slug', {
      // eslint-disable-next-line camelcase
      p_slug: slug
    });

    const { error, data: responseData } = response;

    if (error) {
      return rejectWithValue(error?.message);
    }

    return {
      errorCode: responseData.error_code || null,
      isAvailable: responseData.is_available
    };
  }
);

const registerRestaurantSubdomain = createAsyncThunk(
  'restaurants/registerRestaurantSubdomain',
  async (restaurantId, { dispatch, rejectWithValue }) => {
    try {
      const response = await supabase.functions.invoke('register-restaurant-subdomain', {
        body: { restaurantId }
      });

      const { data, error } = response;

      // Check if the edge function returned an error response (data will contain error details)
      if (data && !data.success) {
        return rejectWithValue({
          error: data.error || 'Failed to register subdomain',
          errorCode: data.errorCode
        });
      }

      // If there's an error but no data, it's a network/connection error
      if (error) {
        return rejectWithValue({
          error: error.message || 'Network error',
          errorCode: null
        });
      }

      // Refetch restaurants to get updated subdomain status
      await dispatch(fetchRestaurants());

      return {
        alreadyExists: data.alreadyExists,
        domain: data.domain,
        restaurantId,
        success: true
      };
    } catch (error) {
      return rejectWithValue({
        error: error?.message || 'Unknown error',
        errorCode: null
      });
    }
  }
);

const checkRestaurantSubdomainStatus = createAsyncThunk(
  'restaurants/checkRestaurantSubdomainStatus',
  async (restaurantId, { dispatch, rejectWithValue }) => {
    try {
      const response = await supabase.functions.invoke('check-restaurant-subdomain-status', {
        body: restaurantId ? { restaurantId } : {}
      });

      const { data, error } = response;

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error('Failed to check subdomain status');
      }

      // Only refetch if status actually changed
      const statusChanged = data.results?.some(result => result.statusChanged);

      if (statusChanged) {
        await dispatch(fetchRestaurants());
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const checkCustomDomainStatus = createAsyncThunk(
  'restaurants/checkCustomDomainStatus',
  async (restaurantId, { dispatch, rejectWithValue }) => {
    try {
      const response = await supabase.functions.invoke('check-custom-domain-status', {
        body: restaurantId ? { restaurantId } : {}
      });

      const { data, error } = response;

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error('Failed to check custom domain status');
      }

      // Only refetch if status actually changed
      const statusChanged = data.results?.some(result => result.statusChanged);

      if (statusChanged) {
        await dispatch(fetchRestaurants());
      }

      return data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const registerCustomDomain = createAsyncThunk(
  'restaurants/registerCustomDomain',
  async ({ restaurantId, customDomain }, { dispatch, rejectWithValue }) => {
    try {
      const response = await supabase.functions.invoke('register-custom-domain', {
        body: { customDomain, restaurantId }
      });

      const { data, error } = response;

      // Check if the edge function returned an error response (data will contain error details)
      if (data && !data.success) {
        return rejectWithValue({
          error: data.error || 'Failed to register custom domain',
          errorCode: data.errorCode
        });
      }

      // If there's an error but no data, it's a network/connection error
      if (error) {
        return rejectWithValue({
          error: error.message || 'Network error',
          errorCode: null
        });
      }

      // Refetch restaurants to get updated domain_config
      await dispatch(fetchRestaurants());

      return {
        dnsInstructions: data.dnsInstructions,
        domain: data.domain,
        verified: data.verified
      };
    } catch (error) {
      return rejectWithValue({
        error: error?.message || 'Unknown error',
        errorCode: null
      });
    }
  }
);

const verifyCustomDomain = createAsyncThunk(
  'restaurants/verifyCustomDomain',
  async (restaurantId, { dispatch, rejectWithValue }) => {
    try {
      const response = await supabase.functions.invoke('verify-custom-domain', {
        body: { restaurantId }
      });

      const { data, error } = response;

      // Check if the edge function returned an error response (data will contain error details)
      if (data && !data.success) {
        return rejectWithValue({
          error: data.error || 'Failed to verify custom domain',
          errorCode: data.errorCode
        });
      }

      // If there's an error but no data, it's a network/connection error
      if (error) {
        return rejectWithValue({
          error: error.message || 'Network error',
          errorCode: null
        });
      }

      // Refetch restaurants to get updated verification status
      await dispatch(fetchRestaurants());

      return {
        domain: data.domain,
        message: data.message,
        verified: data.verified
      };
    } catch (error) {
      return rejectWithValue({
        error: error?.message || 'Unknown error',
        errorCode: null
      });
    }
  }
);

const removeCustomDomain = createAsyncThunk(
  'restaurants/removeCustomDomain',
  async (restaurantId, { dispatch, rejectWithValue }) => {
    try {
      const response = await supabase.functions.invoke('remove-custom-domain', {
        body: { restaurantId }
      });

      const { data, error } = response;

      // Check if the edge function returned an error response (data will contain error details)
      if (data && !data.success) {
        return rejectWithValue({
          error: data.error || 'Failed to remove custom domain',
          errorCode: data.errorCode
        });
      }

      // If there's an error but no data, it's a network/connection error
      if (error) {
        return rejectWithValue({
          error: error.message || 'Network error',
          errorCode: null
        });
      }

      // Refetch restaurants to get updated domain_config
      await dispatch(fetchRestaurants());

      return {
        domain: data.domain,
        message: data.message
      };
    } catch (error) {
      return rejectWithValue({
        error: error?.message || 'Unknown error',
        errorCode: null
      });
    }
  }
);

// Adapter
const restaurantsAdapter = createEntityAdapter();

// Additional Initial State Options
const additionalInitialStateOptions = {
  isCheckingCustomDomainStatus: false,
  isCheckingSubdomainStatus: false,
  isFetchingRestaurants: false,
  isRegisteringCustomDomain: false,
  isRegisteringSubdomain: false,
  isRemovingCustomDomain: false,
  isSavingOrdersState: false,
  isUpdatingRestaurant: false,
  isValidatingRestaurantSlug: false,
  isVerifyingCustomDomain: false,
  shouldUpdateHolidaysData: true,
  shouldUpdateScheduleData: true
};

const restaurantsSlice = createSlice({
  extraReducers: (builder) => {
    builder
      // Create Restaurants Handlers
      .addCase(createRestaurant.pending, (state) => {
        state.isUpdatingRestaurant = true;
      })
      .addCase(createRestaurant.fulfilled, (state, { payload }) => {
        state.isUpdatingRestaurant = false;

        const restaurant = payload;

        if (restaurant) {
          restaurantsAdapter.upsertOne(state, restaurant);

          setUserData(state, {
            payload: {
              hasRestaurant: true
            }
          });
        }
      })
      .addCase(createRestaurant.rejected, (state) => {
        state.isUpdatingRestaurant = false;
      })
      // Fetch Restaurants Handlers
      // Currently our App only handle a single restaurant, but in our database a User may have multiple Restaurants;
      // That's why the fetch response contains an array of Restaurants;
      .addCase(fetchRestaurants.pending, (state) => {
        state.isFetchingRestaurants = true;
      })
      .addCase(fetchRestaurants.fulfilled, (state, { payload }) => {
        state.isFetchingRestaurants = false;
        state.performedInitialFetch = true;

        const restaurants = payload;

        if (Array.isArray(restaurants)) {
          restaurantsAdapter.setAll(state, restaurants);
        }
      })
      .addCase(fetchRestaurants.rejected, (state) => {
        state.isFetchingRestaurants = false;
      })
      // Handle Logout
      .addCase(clearUserData, () => ({
        ...restaurantsAdapter.getInitialState()
      }))
      // Update Restaurants Handlers
      .addCase(updateRestaurant.pending, (state) => {
        state.isUpdatingRestaurant = true;
      })
      .addCase(updateRestaurant.fulfilled, (state, { payload }) => {
        state.isUpdatingRestaurant = false;
        state.shouldUpdateHolidaysData = true;
        state.shouldUpdateScheduleData = true;

        const { restaurant } = payload;

        if (restaurant) {
          restaurantsAdapter.upsertOne(state, restaurant);
        }
      })
      .addCase(updateRestaurant.rejected, (state) => {
        state.isUpdatingRestaurant = false;
      })
      // Set Restaurant Orders State Handlers (sidebar quick controls)
      .addCase(setRestaurantOrdersState.pending, (state) => {
        state.isSavingOrdersState = true;
      })
      .addCase(setRestaurantOrdersState.fulfilled, (state, { payload }) => {
        state.isSavingOrdersState = false;

        if (payload?.id) {
          restaurantsAdapter.upsertOne(state, payload);
        }
      })
      .addCase(setRestaurantOrdersState.rejected, (state) => {
        state.isSavingOrdersState = false;
      })
      // Validate Restaurant Slug Handlers
      .addCase(validateRestaurantSlug.pending, (state) => {
        state.isValidatingRestaurantSlug = true;
      })
      .addCase(validateRestaurantSlug.fulfilled, (state) => {
        state.isValidatingRestaurantSlug = false;
      })
      .addCase(validateRestaurantSlug.rejected, (state) => {
        state.isValidatingRestaurantSlug = false;
      })
      // Register Restaurant Subdomain Handlers
      .addCase(registerRestaurantSubdomain.pending, (state) => {
        state.isRegisteringSubdomain = true;
      })
      .addCase(registerRestaurantSubdomain.fulfilled, (state) => {
        state.isRegisteringSubdomain = false;
      })
      .addCase(registerRestaurantSubdomain.rejected, (state) => {
        state.isRegisteringSubdomain = false;
      })
      // Check Restaurant Subdomain Status Handlers
      .addCase(checkRestaurantSubdomainStatus.pending, (state) => {
        state.isCheckingSubdomainStatus = true;
      })
      .addCase(checkRestaurantSubdomainStatus.fulfilled, (state) => {
        state.isCheckingSubdomainStatus = false;
      })
      .addCase(checkRestaurantSubdomainStatus.rejected, (state) => {
        state.isCheckingSubdomainStatus = false;
      })
      // Check Custom Domain Status Handlers
      .addCase(checkCustomDomainStatus.pending, (state) => {
        state.isCheckingCustomDomainStatus = true;
      })
      .addCase(checkCustomDomainStatus.fulfilled, (state) => {
        state.isCheckingCustomDomainStatus = false;
      })
      .addCase(checkCustomDomainStatus.rejected, (state) => {
        state.isCheckingCustomDomainStatus = false;
      })
      // Register Custom Domain Handlers
      .addCase(registerCustomDomain.pending, (state) => {
        state.isRegisteringCustomDomain = true;
      })
      .addCase(registerCustomDomain.fulfilled, (state) => {
        state.isRegisteringCustomDomain = false;
      })
      .addCase(registerCustomDomain.rejected, (state) => {
        state.isRegisteringCustomDomain = false;
      })
      // Verify Custom Domain Handlers
      .addCase(verifyCustomDomain.pending, (state) => {
        state.isVerifyingCustomDomain = true;
      })
      .addCase(verifyCustomDomain.fulfilled, (state) => {
        state.isVerifyingCustomDomain = false;
      })
      .addCase(verifyCustomDomain.rejected, (state) => {
        state.isVerifyingCustomDomain = false;
      })
      // Remove Custom Domain Handlers
      .addCase(removeCustomDomain.pending, (state) => {
        state.isRemovingCustomDomain = true;
      })
      .addCase(removeCustomDomain.fulfilled, (state) => {
        state.isRemovingCustomDomain = false;
      })
      .addCase(removeCustomDomain.rejected, (state) => {
        state.isRemovingCustomDomain = false;
      });
  },

  initialState: restaurantsAdapter.getInitialState(additionalInitialStateOptions),

  name: 'restaurants',

  reducers: {
    confirmHolidaysUpdate: (state) => {
      state.shouldUpdateHolidaysData = false;
    },

    confirmScheduleUpdate: (state) => {
      state.shouldUpdateScheduleData = false;
    }
  }
});

// Actions
const {
  confirmHolidaysUpdate,
  confirmScheduleUpdate
} = restaurantsSlice.actions;

// Selectors
const restaurantsSelectors = restaurantsAdapter.getSelectors(state => state.restaurants);

const { selectAll: selectRestaurants } = restaurantsSelectors;

const selectCurrentRestaurant = createSelector(
  selectRestaurants,
  restaurants => restaurants[0]
);

const selectIsFetchingRestaurants = state => state.restaurants?.isFetchingRestaurants;

const selectIsUpdatingRestaurant = state => state.restaurants?.isUpdatingRestaurant;

const selectIsValidatingRestaurantSlug = state => state.restaurants?.isValidatingRestaurantSlug;

const selectIsRegisteringSubdomain = state => state.restaurants?.isRegisteringSubdomain;

const selectIsCheckingCustomDomainStatus = state => state.restaurants?.isCheckingCustomDomainStatus;
const selectIsCheckingSubdomainStatus = state => state.restaurants?.isCheckingSubdomainStatus;

const selectIsRegisteringCustomDomain = state => state.restaurants?.isRegisteringCustomDomain;

const selectIsVerifyingCustomDomain = state => state.restaurants?.isVerifyingCustomDomain;

const selectIsRemovingCustomDomain = state => state.restaurants?.isRemovingCustomDomain;

const selectIsSavingOrdersState = state => state.restaurants?.isSavingOrdersState;

const selectShouldUpdateHolidaysData = state => state.restaurants?.shouldUpdateHolidaysData;

const selectShouldUpdateScheduleData = state => state.restaurants?.shouldUpdateScheduleData;

export {
  checkCustomDomainStatus,
  checkRestaurantSubdomainStatus,
  confirmHolidaysUpdate,
  confirmScheduleUpdate,
  createRestaurant,
  fetchRestaurants,
  registerCustomDomain,
  registerRestaurantSubdomain,
  removeCustomDomain,
  selectCurrentRestaurant,
  selectIsCheckingCustomDomainStatus,
  selectIsCheckingSubdomainStatus,
  selectIsFetchingRestaurants,
  selectIsRegisteringCustomDomain,
  selectIsRegisteringSubdomain,
  selectIsRemovingCustomDomain,
  selectIsSavingOrdersState,
  selectIsUpdatingRestaurant,
  selectIsValidatingRestaurantSlug,
  selectIsVerifyingCustomDomain,
  selectRestaurants,
  selectShouldUpdateHolidaysData,
  selectShouldUpdateScheduleData,
  setRestaurantOrdersState,
  updateRestaurant,
  validateRestaurantSlug,
  verifyCustomDomain
};

export default restaurantsSlice.reducer;
